import * as _ from 'lodash-es';
import Quill, { Delta } from 'quill/core';
import List from 'quill/formats/list';
import React, { useEffect, useState } from 'react';
import getSlashCommandMenuSections, {
  CommandMenuMatchKey,
  INSERT_TYPES,
} from './getSlashCommandMenuSections';
import PrefixTriggeredMenu from './PrefixTriggeredMenu';
import useAppSelector from '../../app/redux/useAppSelector';
import { MenuItem } from '../../components/Menu';
import Tooltip from '../../components/Tooltip';
import Uploader from '../../components/Uploader';
import LocalSearchHighlight from '../../search/LocalSearchHighlight';
import exhaustiveMatchGuard from '../../utils/exhaustiveMatchGuard';
import getFeature from '../../utils/features';
import useIsPublicView from '../../utils/useIsPublicView';
import attachmentSizeExceededToast from '../attachmentSizeExceededToast';
import convertSelectionToCodeEmbed from '../code/convertSelectionToCodeEmbed';
import insertCodeEmbed from '../code/insertCodeEmbed';
import { EDITOR_IMAGE_ACCEPT } from '../editorImageMimeTypes';
import * as styles from './SlashCommandMenu.module.scss';
import Block from '../formats/Block';
import Blockquote from '../formats/Blockquote';
import Header from '../formats/Header';
import Hint from '../formats/Hint';
import { usePrefixListener } from '../PrefixListener';
import generateDeltaRandomId from '../utils/generateDeltaRandomId';
import useShowEditorToast from '../utils/useShowEditorToast';

const BLOT_WHITELIST = new Set(
  [Block, Blockquote, Header, Hint, List].map((blot) => blot.blotName),
);

// Create a new line if the cursor is in the middle of the line.
// This is actually what Quill does when inserting embeds in the middle of
// a line. We do it explicitly here to shift the selection if necessary.
const createNewLineIfNecessary = (quill: Quill, index: number) => {
  const [line, offset] = quill.getLine(index);
  if (line && offset !== 0) {
    line.split(offset);
    quill.update();

    // + `1` for the "\n" created by #split()
    quill.setSelection(index + 1, Quill.sources.SILENT);
    return index + 1;
  }
  return index;
};

const USE_PREFIX_LISTENER_OPTIONS = ['/'];

type TooltipType = {
  label: string;
  bounds: ReturnType<Quill['getBounds']>;
};

type Dropzone = {
  attachment: boolean;
  style: React.CSSProperties;
};

interface SlashCommandMenuProps {
  quill: Quill;
  readOnly: boolean;
  imageTooltip?: string;
}

const SlashCommandMenu = ({
  quill,
  readOnly,
  imageTooltip,
}: SlashCommandMenuProps) => {
  const [tooltip, setTooltip] = useState<TooltipType | null>(null);
  const [dropzone, setDropzone] = useState<Dropzone | null>(null);
  const { blot, bounds, text, reset } = usePrefixListener(
    quill,
    USE_PREFIX_LISTENER_OPTIONS,
  );
  const query = text ? text.toUpperCase() : '';
  const lastCodeBlockSyntax = useAppSelector(
    (state) => state.ui.lastCodeBlockSyntax,
  );
  const isPublicView = useIsPublicView();

  const isShowingTooltipOrDropzone = !!(tooltip || dropzone);
  useEffect(() => {
    if (!isShowingTooltipOrDropzone) {
      return undefined;
    }

    const editorChangeListener = (
      type: string,
      newValue: Delta,
      _oldValue: Delta,
      source: string,
    ) => {
      if (source === Quill.sources.USER) {
        setTooltip(null);

        if (type !== Quill.events.SELECTION_CHANGE || newValue !== null) {
          setDropzone(null);
        }
      }
    };

    quill.on(Quill.events.EDITOR_CHANGE, editorChangeListener);
    return () => {
      quill.off(Quill.events.EDITOR_CHANGE, editorChangeListener);
    };
  }, [quill, isShowingTooltipOrDropzone]);

  const showEditorToast = useShowEditorToast();

  const { sections, getAction } = getSlashCommandMenuSections({
    query,
    scroll: quill.scroll,
    publicView: isPublicView,
    onFormat(attributes) {
      Object.keys(attributes).forEach((name) => {
        quill.format(name, attributes[name], Quill.sources.USER);
      });
    },
    onInsert(type, index) {
      switch (type) {
        case INSERT_TYPES.TABLE_EMBED: {
          const newIndex = createNewLineIfNecessary(quill, index);
          quill.insertEmbed(
            newIndex,
            'table-embed',
            {
              rows: new Array(2)
                .fill(0)
                .map(() => ({ insert: { id: generateDeltaRandomId() } })),
              columns: new Array(2)
                .fill(0)
                .map(() => ({ insert: { id: generateDeltaRandomId() } })),
            },
            Quill.sources.USER,
          );
          const [line] = quill.getLine(newIndex);
          _.defer(async () => {
            if (
              line?.statics.blotName === 'table-embed' &&
              'getOrCreateCellQuill' in line &&
              typeof line.getOrCreateCellQuill === 'function'
            ) {
              const cellQuill = await line.getOrCreateCellQuill('1:1');
              cellQuill?.setSelection(0, Quill.sources.USER);
            }
          });
          break;
        }
        case INSERT_TYPES.CODE_BLOCK: {
          convertSelectionToCodeEmbed(quill, index, 0, lastCodeBlockSyntax);
          break;
        }
        case INSERT_TYPES.IMAGE:
        case INSERT_TYPES.ATTACHMENT: {
          if (type === INSERT_TYPES.IMAGE && imageTooltip) {
            setTooltip({
              label: imageTooltip,
              bounds: quill.getBounds(index),
            });
            break;
          }
          const newIndex = createNewLineIfNecessary(quill, index);
          const [line] = quill.getLine(newIndex);
          if (line) {
            const lineRect = line.domNode.getBoundingClientRect();
            const containerRect = quill.container.getBoundingClientRect();
            setDropzone({
              attachment: type === INSERT_TYPES.ATTACHMENT,
              style: {
                position: 'absolute',
                height: lineRect.height,
                width: lineRect.width,
                top: lineRect.top - containerRect.top,
                left: lineRect.left - containerRect.left,
              },
            });
          }
          break;
        }

        case INSERT_TYPES.DIVIDER: {
          const embedIndex = createNewLineIfNecessary(quill, index);
          quill.insertEmbed(embedIndex, 'hr', true, Quill.sources.USER);
          quill.setSelection(embedIndex + 1, Quill.sources.USER);
          break;
        }
        case INSERT_TYPES.FLOWCHART: {
          const newIndex = createNewLineIfNecessary(quill, index);

          insertCodeEmbed({
            quill,
            index: newIndex,
            language: 'mermaid',
            embedDelta: new Delta()
              .insert('%% See supported syntax: ')
              .insert('https://go.slab.com/mermaid', {
                link: 'https://go.slab.com/mermaid',
              })
              .insert('\n\nflowchart LR\n  Cause --> Effect\n'),
          });
          break;
        }
        case INSERT_TYPES.EQUATION: {
          const newIndex = createNewLineIfNecessary(quill, index);

          insertCodeEmbed({
            quill,
            index: newIndex,
            language: 'latex',
            embedDelta: new Delta()
              .insert('%% See supported syntax: ')
              .insert('https://go.slab.com/katex', {
                link: 'https://go.slab.com/katex',
              })
              .insert('\n\na^2 + b^2 = c^2\n'),
          });
          break;
        }
        case INSERT_TYPES.MENTION_USER:
          quill.insertText(index, '@', Quill.sources.USER);
          quill.setSelection(index + 1, Quill.sources.SILENT);
          break;

        case INSERT_TYPES.MENTION_POST:
          quill.insertText(index, '+', Quill.sources.USER);
          quill.setSelection(index + 1, Quill.sources.SILENT);
          break;
        case INSERT_TYPES.BUTTON: {
          const newIndex = createNewLineIfNecessary(quill, index);

          const delta = new Delta().retain(newIndex).insert({
            button: {
              type: 'navigation',
              destination: '',
            },
          });
          quill.updateContents(delta, Quill.sources.USER);
          break;
        }
        default:
          exhaustiveMatchGuard(type);
      }
    },
    onShowTooltip(label, index) {
      setTooltip({
        label,
        bounds: quill.getBounds(index),
      });
    },
  });

  const handleDropFiles = (files: File[], forceAttachment: boolean) => {
    setDropzone(null);

    const range = quill.getSelection(true);
    // @ts-expect-error
    return quill.uploader?.handleUpload
      ? // @ts-expect-error
        quill.uploader.handleUpload(range, files, forceAttachment)
      : Promise.reject(new Error('No upload handler found'));
  };

  return readOnly ? null : (
    <>
      {tooltip && (
        <Tooltip content={tooltip.label} open asChild>
          <div
            className={styles.tooltipTarget}
            style={tooltip.bounds ?? undefined}
          />
        </Tooltip>
      )}
      {dropzone && (
        <div className={styles.dropzone} style={dropzone.style}>
          <Uploader
            accept={dropzone.attachment ? undefined : EDITOR_IMAGE_ACCEPT}
            subject={dropzone.attachment ? 'files' : 'images'}
            image={!dropzone.attachment}
            onDrop={(files) => handleDropFiles(files, dropzone.attachment)}
            multiple
            maxSizeMB={getFeature('attachment_size_limit')}
            onMaxSizeError={() => {
              showEditorToast(attachmentSizeExceededToast);
            }}
          />
        </div>
      )}
      {bounds && BLOT_WHITELIST.has(blot.statics.blotName) && (
        <PrefixTriggeredMenu
          quill={quill}
          onSelect={({ id }) => {
            const range = quill.getSelection(true);
            if (range == null) return;
            const length = text.length + 1;
            quill.deleteText(range.index - length, length, Quill.sources.USER);

            const action = getAction(id);
            if (action) {
              action(range.index - length);
            }
          }}
          sections={sections}
          query={query}
          withMenuPadding
          onClose={reset}
          bounds={bounds}
          renderItem={({ label, matches, icon }) => (
            <MenuItem icon={icon}>
              <LocalSearchHighlight
                matches={matches}
                matchKey={CommandMenuMatchKey.Label}
                defaultValue={label}
              />
            </MenuItem>
          )}
          containerSize="small"
          testId="commandMenu"
        />
      )}
    </>
  );
};

export default SlashCommandMenu;
